import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function DocLink({ navigate, to, children }) {
  return (
    <button onClick={() => navigate(to)} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">
      {children}
    </button>
  );
}

function Lbl({ children }) {
  return <strong className="text-white">{children}</strong>;
}

function PageAutopilot({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">
        Play &mdash; Running Routes
      </h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The <strong className="text-white">AUTOPILOT &rsaquo; Play</strong> tab starts the bot. Out
        of the box it shows <strong className="text-white">ready-made runs</strong>: pick one, press
        START, and the bot walks the maps, fights, loots and goes through the gates on its own &mdash;
        no recording, no editor. If you build your own routes, switch on{' '}
        <strong className="text-white">Advanced mode</strong> and the tab shows your routes instead
        (see the second half of this page). While a run is on, a green dot shows next to{' '}
        <strong className="text-white">Play</strong> in the list on the left.
      </p>

      <p className="text-sm text-gray-400 mb-4">
        Two chips sit at the top of the tab in both modes: <Lbl>Advanced mode</Lbl> (switches
        between the two layouts; the choice is remembered) and <Lbl>Show the rail</Lbl> (draws the
        path the run follows on the ground).
      </p>

      <DocHeading>Ready-Made Runs</DocHeading>
      <Step n={1} title="Pick a group">
        The list on the left of the lower card has four groups: <strong>Blood chests</strong>,{' '}
        <strong>Parallel Worlds</strong>, <strong>Events</strong> and <strong>World bosses</strong>.
        Each shows what it does in one line; hover the <strong>i</strong> for more.
      </Step>
      <Step n={2} title="Check the settings">
        The card shows the chosen <strong>Map</strong>, <strong>Difficulty</strong> and the options
        of the run. Press <strong>Settings</strong> to change them. Maps without a built-in path are
        grey and marked <em>Not ready yet</em> &mdash; more are added with updates.
      </Step>
      <Step n={3} title="Press START">
        The STATUS card turns <span className="text-emerald-400">RUNNING</span>. Press{' '}
        <strong>STOP</strong> whenever you want. Only one run can go at a time: while one runs, the
        START of the others says <em>Stop &hellip; first</em>.
      </Step>

      <Shot
        name="autopilot-01-play-tab.png"
        tab="AUTOPILOT › Play"
        caption="A Parallel Worlds run on Grimmagstone, on the second of its three maps."
        shoot="menushot (stage route simple-run + route_ui pw): egyszerű mód, a Parallel Worlds csoport, Grimmagstone fut — STATUS: RUNNING, Run / Doing / Where, Map, Step map 2 of 3."
      />

      <DocHeading>The Settings Window</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">Settings</strong> button opens the options of the
        selected group. What you see depends on the run:
      </p>
      <DocTable
        headers={['Option', 'What it does']}
        rows={[
          [<Lbl>Map</Lbl>, 'Which map the run goes to. Maps that are not ready yet are listed but cannot be picked.'],
          [<Lbl>Difficulty</Lbl>, 'The difficulty to enter, as the game names it.'],
          [<Lbl>Each lap</Lbl>, <>Parallel Worlds only. <strong>Whole chain</strong>: every lap goes through all three maps. <strong>From map 2</strong>: after the first lap it teleports to the second map. <strong>Boss room only</strong>: after the first lap it teleports straight into the boss room &mdash; each entry costs what the game charges for it.</>],
          [<Lbl>Kill the boss before the chest</Lbl>, 'On by default. The run stops at the boss and waits until it is dead, then walks to the chest. Off: straight to the chest.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        The window also has its own START and STOP. <em>Changes apply to the next START. It keeps
        running until you press STOP.</em>
      </p>

      <Shot
        name="autopilot-02-run-settings.png"
        tab="AUTOPILOT › Play › Settings"
        caption="The settings of the Parallel Worlds run: map, difficulty, what each lap does, and the boss."
        shoot="menushot (stage route_ui pw-settings): a Parallel Worlds menet Settings ablaka (Map, Difficulty, Each lap, Kill the boss before the chest), mögötte a futó menet."
      />

      <DocHeading>Before It Starts</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        A run will not start until it can finish. START stays disabled, and the reason is written
        next to it:
      </p>
      <DocTable
        headers={['Message', 'What to do']}
        rows={[
          [<em>The fight is off - turn it on in the COMBAT tab.</em>, <>Every run fights. Set up <DocLink navigate={navigate} to="auto-combat">Combat</DocLink> first.</>],
          [<em>No skill is turned on - set up your skills in the COMBAT tab.</em>, 'Add your skills on the Combat tab.'],
          [<em>COMBAT is set to Do not fight - pick what it should attack.</em>, 'Pick Everything nearby or Only what blocks me.'],
          [<em>Not ready yet - this map has no built-in path.</em>, 'Pick another map in Settings.'],
          [<em>Blood chests only appear on Bloodshed.</em>, 'Pick the Bloodshed difficulty for a blood chest run.'],
          [<em>No Anniversary Arena Chip in your bag.</em>, 'That event run is entered with its chip. Get one first.'],
        ]}
      />
      <Callout type="info">
        Starting a <strong>Parallel Worlds</strong> run turns on{' '}
        <DocLink navigate={navigate} to="auto-gather">Gather</DocLink> and{' '}
        <DocLink navigate={navigate} to="auto-loot">Loot</DocLink> for you, with the longest range,
        the shortest delay and every category. If you had them set up already, nothing is touched.
        A <strong>Blood chests</strong> run switches on the{' '}
        <DocLink navigate={navigate} to="bloodshed">Bloodshed</DocLink> timer.
      </Callout>

      <DocHeading>The STATUS Card</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The badge in the corner says <span className="text-emerald-400">RUNNING</span>,{' '}
        <span className="text-yellow-300">WAITING</span>,{' '}
        <span className="text-amber-300">STOPPED</span> or <span className="text-gray-400">IDLE</span>.
      </p>
      <DocTable
        headers={['Line', 'What it shows']}
        rows={[
          [<Lbl>Run</Lbl>, 'Which group is running.'],
          [<Lbl>Map</Lbl>, 'The map of the run.'],
          [<Lbl>Doing</Lbl>, <><em>following the route</em>, <em>going through the gate</em>, <em>on its way to the next map</em>, or what the movement is doing.</>],
          [<Lbl>Waiting for</Lbl>, <>Shown instead of Doing while the run waits: <em>fighting</em>, <em>the boss</em>, <em>the blood chests</em>, <em>picking up loot</em>, <em>gathering</em>, <em>coming back to life</em>, <em>getting on the mount</em>, <em>a waiting point</em>, <em>a monster it has to kill</em>.</>],
          [<Lbl>Step</Lbl>, <>Which map of the chain, for example <em>map 2 of 3</em>.</>],
          [<Lbl>Where</Lbl>, 'Your position on the map.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        When a run stops by itself, the card keeps the reason in orange with{' '}
        <em>Press START to try again. If it keeps happening, tell us which map.</em> A run you
        stopped yourself says <em>Press START to go again.</em>
      </p>

      <DocHeading>Advanced Mode: Your Own Routes</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        With <Lbl>Advanced mode</Lbl> on, the tab runs the routes you recorded yourself (on{' '}
        <DocLink navigate={navigate} to="route-builder">AUTOPILOT &rsaquo; Editor</DocLink>, which
        only shows in this mode). A route is one step per map, plus the gate or teleport that leads
        on to the next map.
      </p>

      <Shot
        name="autopilot-03-advanced-mode.png"
        tab="AUTOPILOT › Play (Advanced mode)"
        caption="Advanced mode: a loaded route that is not running yet, and MY ROUTES with two folders."
        shoot="menushot (stage route adv + route_ui adv): Advanced mode, betöltött „grimmag merciless” route (3 lépés, step 2 of 3), MY ROUTES a „Bloodshed” és a nyitott „Boss farm” mappával, a Folder mezőben „Boss farm”."
      />

      <Step n={1} title="Load a route">
        Click a route in <strong>MY ROUTES</strong> and press <strong>LOAD</strong>. The route you
        had loaded last time comes back by itself when the bot starts &mdash; loaded, never started.
      </Step>
      <Step n={2} title="Be where the route can start">
        The <strong>Position</strong> line says <em>step N of M</em> when you are on one of its maps,{' '}
        <em>elsewhere - it teleports in</em> when the route starts with a teleport, or{' '}
        <em>not on this route</em> when you have to get to one of its maps first.
      </Step>
      <Step n={3} title="Press START">
        The status reads <span className="text-emerald-400">RUNNING - step N of M</span>. STOP stops
        the character where it stands.
      </Step>

      <DocHeading>STATUS in Advanced Mode</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<span className="text-emerald-400">READY - N steps</span>, 'A route is loaded and nothing blocks it.'],
          [<span className="text-emerald-400">RUNNING - step N of M</span>, 'The run is on.'],
          [<span className="text-yellow-300">RECORDING step N of M - walk the path</span>, 'A recording is open on the Editor tab. Finish it there first.'],
          [<span className="text-gray-400">NO ROUTE - build one on the Editor tab</span>, 'Nothing is loaded.'],
          [<span className="text-red-400">N PROBLEMS - see the Editor tab</span>, 'Something is missing from the route, for example a step with no exit. START stays blocked until it is fixed.'],
          [<>The line below</>, <>The last thing that happened, e.g. <em>Loaded &#39;&hellip;&#39;</em>, <em>Lap 3 done - back on step 1 (&hellip;).</em> A stop is kept there too: <em>Finished 2 min ago: &hellip;</em> or <em>Last stop 40 s ago: &hellip;</em></>],
          [<Lbl>Map</Lbl>, <>The map you are on, as the game names it internally. A red <em>unknown</em> is normal during a loading screen.</>],
          [<Lbl>Following step / You are on step</Lbl>, 'While running: the step the run works on, and the step of the map you stand on.'],
          [<Lbl>Gate</Lbl>, <>While running, what the run is doing with the maps: <em>starting</em>, <em>entering</em>, <em>landing</em>, <em>following</em>, <em>at a point</em>, <em>exiting</em>, <em>going through</em>.</>],
          [<Lbl>Movement</Lbl>, <>What the character does: <em>following</em> (green), or <em>rejoining</em>, <em>reaching</em>, <em>waiting</em>, <em>recovering</em>, <em>paused</em> with the reason after the colon (orange).</>],
        ]}
      />
      <Callout type="warning">
        <strong>&ldquo;Waiting - this map is not part of the route.&rdquo;</strong> You ended up
        somewhere the route does not know: a town, a wrong portal. There is no time limit. Walk or
        teleport back to any map of the route and it picks itself up.
      </Callout>

      <DocHeading>The WHILE IT RUNS Card</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<Lbl>Loop at the end</Lbl>, 'At the end of the lap it starts over instead of stopping.'],
          [<Lbl>Only map 2</Lbl>, 'Parallel World chains only. The first lap goes through all three maps, then after the boss room it teleports to the second map and goes on from there.'],
          [<Lbl>Only boss room</Lbl>, 'Parallel World chains only. On the second map it does not run to the gate but teleports straight into the boss room, and after the boss room back into it, every lap. It needs a boss room entry. Only one of the two can be on.'],
          [<Lbl>Kill the boss</Lbl>, 'In the boss room the run stops at the boss and waits until it is dead, then goes to the chest. It takes effect right away, even during a run.'],
          [<Lbl>+Kill buttons on monsters</Lbl>, 'Puts a small button over every monster in the world, to add it to this route’s kill list or take it off. It edits the route, so it is a building tool.'],
          [<Lbl>START / STOP</Lbl>, 'Starts the run, or stops it on the spot.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        With the +Kill buttons on, stand on a map of the route and click a monster. Marks go by
        monster type <em>and</em> rank. The card says <em>Click a monster in the world.</em>, or{' '}
        <em>Not a step - add it on the Editor tab.</em> when the map is not part of the route. A
        helper object the game does not let you target says{' '}
        <em>the game says this cannot be targeted</em> &mdash; do not mark those. Marks are changes
        to the route: save it on the Editor tab. A monster on the kill list is always fought,
        whatever the Combat tab&rsquo;s Fight option says.
      </p>

      <DocHeading>How a Run Ends and Loops</DocHeading>
      <DocTable
        headers={['The route ends with', 'Loop on', 'Loop off']}
        rows={[
          ['An exit from the last map back to the first one', <><em>Lap N done - back on step 1 (&hellip;).</em></>, <><em>the route finished - step N was the last (loop is off)</em></>],
          ['A gate that leads out of the route', '—', <><em>arrived in &hellip; - the route ends here</em></>],
          ['No exit on the last step (a circuit on one map, or a run that starts with a teleport)', <>Starts over: <em>Lap N done - starting over.</em> A circuit must end within 15 m of where it began.</>, 'Stops at the end of the path.'],
        ]}
      />

      <DocHeading>Why Did It Stop?</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        A run that stops by itself writes <em>Route stopped: &hellip;</em> in red. The ones you are
        most likely to meet:
      </p>
      <DocTable
        headers={['Reason', 'What to do']}
        rows={[
          [<em>You are not on any map of this route.</em>, 'Go to one of its maps, or give the route a teleport at the start.'],
          [<em>loop is on, but the path does not return to its start - &hellip;</em>, 'Re-record the path as a circuit, or add an exit to the last step.'],
          [<em>that destination no longer exists - set the exit again</em>, 'The exit points somewhere that is not there any more (usually after a game update). Set it again on the Editor tab.'],
          [<em>the teleport exit has no destination</em>, 'Set the teleport on the Editor tab.'],
          [<em>the teleport item was used but did not take us there - &hellip;</em>, 'It is not retried on purpose, so a second item is not wasted. Press START again.'],
          [<em>the entry item was used twice but did not take us in - &hellip;</em>, 'The entry did not work twice in a row. Check the entry item and the difficulty.'],
          [<em>the bag could not be read - no item was used</em>, 'The game was loading. Press START again.'],
        ]}
      />

      <DocHeading>My Routes</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The right column lists every route you have saved. <em>Saved in the data folder. A route
        belongs to a map - the bot only offers the ones that fit where you stand.</em> Routes without
        a folder come first; each folder is a row you can open, with the number of routes in
        brackets, and the folder of the selected route opens by itself.
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<Lbl>LOAD</Lbl>, <>Loads the selected route. <em>Pick a route from the list first.</em> if nothing is selected; <em>Stop what is running first.</em> during a run or a recording.</>],
          [<Lbl>DELETE</Lbl>, <>Turns into <strong>REALLY DELETE</strong>; the second click removes the file. An empty folder disappears too.</>],
          [<Lbl>Folder (empty = no folder)</Lbl>, 'Shows the folder of the selected route. Type a folder name, then press MOVE.'],
          [<Lbl>MOVE</Lbl>, <>Moves the route into that folder (a new folder is created by itself; an empty box takes it out of its folder). It never overwrites: <em>There is already a route called &#39;&hellip;&#39; there.</em></>],
        ]}
      />
      <Callout type="warning">
        <strong>LOAD does not ask.</strong> Loading replaces the route that is open now. If it has
        unsaved changes (the Editor tab shows <strong>Unsaved changes</strong>), save it first.
      </Callout>

      <DocHeading>What Runs Alongside a Run</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The autopilot moves the character. Everything else is its own feature with its own switch,
        and the run waits for it when it needs to:
      </p>
      <DocTable
        headers={['Feature', 'What it adds to a run']}
        rows={[
          [<DocLink navigate={navigate} to="auto-combat">Combat</DocLink>, 'Fights what you meet; the run waits while it fights. Kill targets are always fought.'],
          [<DocLink navigate={navigate} to="party">Party</DocLink>, 'Several characters run together; the run waits for the team at map changes.'],
          [<DocLink navigate={navigate} to="auto-revive">Revive</DocLink>, 'Gets you up after a death; the run waits meanwhile.'],
          [<DocLink navigate={navigate} to="auto-potion">Potion</DocLink> / <DocLink navigate={navigate} to="auto-essence">Essence</DocLink>, 'Keep you alive and your essence slot filled.'],
          [<DocLink navigate={navigate} to="auto-loot">Loot</DocLink> / <DocLink navigate={navigate} to="auto-gather">Gather</DocLink>, 'Pick up and gather on the way; the run waits for dropped loot.'],
          [<DocLink navigate={navigate} to="bloodshed">Bloodshed</DocLink>, 'The blood chest point of a run waits for the chests and opens them.'],
          [<DocLink navigate={navigate} to="auto-sell">Sell</DocLink> / <DocLink navigate={navigate} to="auto-melt">Melt</DocLink>, 'Clear the bag; they finish before the run leaves a map.'],
          [<DocLink navigate={navigate} to="fast-mount">Fast Mount</DocLink>, 'Keeps you mounted between fights.'],
        ]}
      />
    </div>
  );
}

export default PageAutopilot;
